import React from 'react';
import { Box, Skeleton, alpha, type SxProps, type Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { visuallyHiddenSx } from 'utils/a11y';

interface LoadingSkeletonProps {
  rows?: number;
  height?: number;
  /** Forma del placeholder. `bars` es la lista genérica; el resto imita la tienda. */
  variant?: 'bars' | 'page' | 'cards' | 'detail' | 'section' | 'tiles' | 'hero';
  /** Cantidad de tarjetas o mosaicos. */
  count?: number;
  /** Línea de título encima de la grilla. */
  heading?: boolean;
}

const boneSx: SxProps<Theme> = {
  bgcolor: (theme) => (theme.palette.mode === 'light' ? '#e7eaee' : 'rgba(255,255,255,0.12)'),
};

const cardGridSx: SxProps<Theme> = {
  display: 'grid',
  width: '100%',
  gap: 2,
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'repeat(3, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
  },
};

const ProductCardSkeleton: React.FC = () => (
  <Box
    sx={{
      borderRadius: '12px',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    }}
  >
    <Skeleton variant="rectangular" animation="wave" sx={{ height: { xs: 120, sm: 168 }, ...boneSx }} />
    <Box sx={{ p: 1.5 }}>
      <Skeleton animation="wave" width="38%" height={12} sx={boneSx} />
      <Skeleton animation="wave" width="92%" height={18} sx={{ mt: 1, ...boneSx }} />
      <Skeleton animation="wave" width="64%" height={18} sx={boneSx} />
      <Skeleton animation="wave" width="42%" height={22} sx={{ mt: 1.5, ...boneSx }} />
    </Box>
  </Box>
);

const TileSkeleton: React.FC = () => (
  <Box>
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{ height: { xs: 96, md: 120 }, borderRadius: 2, ...boneSx }}
    />
    <Skeleton animation="wave" width="58%" height={14} sx={{ mt: 1, mx: 'auto', ...boneSx }} />
  </Box>
);

const HeadingBone: React.FC = () => (
  <Skeleton animation="wave" width={200} height={32} sx={{ mb: 2, ...boneSx }} />
);

const CardGrid: React.FC<{ count: number }> = ({ count }) => (
  <Box sx={cardGridSx}>
    {Array.from({ length: count }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </Box>
);

const HeroBone: React.FC = () => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 3,
      minHeight: { xs: 220, md: 320 },
      mb: 4,
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
    }}
  >
    <Skeleton
      variant="rectangular"
      animation="wave"
      sx={{
        position: 'absolute',
        inset: 0,
        height: '100%',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
      }}
    />
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        minHeight: { xs: 220, md: 320 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 3, md: 6 },
        gap: 1.25,
        maxWidth: 460,
      }}
    >
      <Box sx={{ height: 28, width: '72%', borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22) }} />
      <Box sx={{ height: 14, width: '90%', borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14) }} />
      <Box sx={{ height: 14, width: '64%', borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14) }} />
      <Box sx={{ height: 36, width: 132, borderRadius: 2, mt: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.32) }} />
    </Box>
  </Box>
);

const PageSkeleton: React.FC = () => (
  <Box>
    <HeroBone />
    <HeadingBone />
    <Box sx={{ ...cardGridSx, mb: 4 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <TileSkeleton key={index} />
      ))}
    </Box>
    <HeadingBone />
    <CardGrid count={4} />
  </Box>
);

const DetailSkeleton: React.FC = () => (
  <Box>
    <Skeleton animation="wave" width={240} height={16} sx={{ mb: 2, ...boneSx }} />
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 2, md: 4 },
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' },
        alignItems: 'start',
      }}
    >
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{ height: { xs: 260, md: 420 }, borderRadius: 2, ...boneSx }}
      />
      <Box sx={{ pt: { md: 1 } }}>
        <Skeleton animation="wave" width="36%" height={14} sx={boneSx} />
        <Skeleton animation="wave" width="88%" height={34} sx={{ mt: 1.5, ...boneSx }} />
        <Skeleton animation="wave" width="52%" height={18} sx={{ mt: 1, ...boneSx }} />
        <Skeleton animation="wave" width="100%" height={14} sx={{ mt: 3, ...boneSx }} />
        <Skeleton animation="wave" width="94%" height={14} sx={{ mt: 0.75, ...boneSx }} />
        <Skeleton animation="wave" width="70%" height={14} sx={{ mt: 0.75, ...boneSx }} />
        <Skeleton animation="wave" width="34%" height={36} sx={{ mt: 3, ...boneSx }} />
        <Skeleton
          variant="rounded"
          animation="wave"
          width={168}
          height={42}
          sx={{ mt: 3, borderRadius: 2, ...boneSx }}
        />
      </Box>
    </Box>
    <Box sx={{ mt: 6 }}>
      <HeadingBone />
      <CardGrid count={4} />
    </Box>
  </Box>
);

const BarSkeleton: React.FC<{ rows: number; height: number }> = ({ rows, height }) => (
  <Box>
    {Array.from({ length: rows }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        animation="wave"
        height={height}
        sx={{ mb: 1.5, borderRadius: 2, ...boneSx }}
      />
    ))}
  </Box>
);

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  rows = 3,
  height = 48,
  variant = 'bars',
  count = 8,
  heading = false,
}) => {
  const { t } = useTranslation('common');

  let body: React.ReactNode;
  if (variant === 'page') body = <PageSkeleton />;
  else if (variant === 'hero') body = <HeroBone />;
  else if (variant === 'detail') body = <DetailSkeleton />;
  else if (variant === 'tiles') {
    body = (
      <Box>
        <HeadingBone />
        <Box sx={cardGridSx}>
          {Array.from({ length: count || 4 }).map((_, index) => (
            <TileSkeleton key={index} />
          ))}
        </Box>
      </Box>
    );
  } else if (variant === 'section' || variant === 'cards') {
    const cards = variant === 'section' ? Math.min(count, 4) : count;
    body = (
      <Box>
        {heading || variant === 'section' ? <HeadingBone /> : null}
        <CardGrid count={cards} />
      </Box>
    );
  } else {
    body = <BarSkeleton rows={rows} height={height} />;
  }

  return (
    <Box role="status" aria-live="polite" aria-busy="true">
      <Box component="span" sx={visuallyHiddenSx}>
        {t('feedback.loading')}
      </Box>
      {body}
    </Box>
  );
};

export default LoadingSkeleton;
